import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'

// Connects to data-controller="stripe-payment"
export default class extends Controller {
  static targets = ['errorContainer', 'submitBtn', 'loaderModal']

  static values = {
    publishableKey: String,
    clientSecret: String,
    stripeAccount: String,
    debug: Boolean,
    validateUrl: String,
  }

  connect() {
    this.submitted = false

    // Wait for Stripe.js to load
    if (typeof Stripe === 'undefined') {
      this.log('Waiting for Stripe.js to load...')
      setTimeout(() => this.connect(), 100)
      return
    }

    this.initializeStripe()
  }

  initializeStripe() {
    this.log('Initializing Stripe...')
    this.log('Client Secret:', this.clientSecretValue)
    this.log('Stripe Account:', this.stripeAccountValue)
    this.log('Stripe Public Key:', this.publishableKeyValue)

    // Initialize Stripe with account ID if provided
    const stripeOptions = this.stripeAccountValue
      ? { stripeAccount: this.stripeAccountValue }
      : {}

    this.stripe = Stripe(this.publishableKeyValue, stripeOptions)

    // Configure appearance to match original code
    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#32325d',
        fontFamily:
          'Helvetica Neue, Arial, Helvetica, Myriad Pro, Lucida Grande, sans-serif',
        fontSize: '14px',
      },
    }

    // Create elements with client secret and appearance
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: appearance,
    })

    // Create and mount payment element
    this.paymentElement = this.elements.create('payment', {
      wallets: {
        applePay: 'auto',
        googlePay: 'auto',
      },
    })

    this.paymentElement.mount('#payment-element')

    // Debug logging for payment element events
    this.paymentElement.on('ready', () => {
      this.log('Payment Element ready')
    })

    this.paymentElement.on('loaderstart', () => {
      this.log('Payment Element loading...')
    })

    this.paymentElement.on('change', (event) => {
      this.log('Payment method changed:', event)
    })

    // Initialize Bootstrap modal if target exists
    if (this.hasLoaderModalTarget) {
      this.modal = new Modal(this.loaderModalTarget, {
        backdrop: 'static',
        keyboard: false,
      })
    }
  }

  async handleSubmit(event) {
    event.preventDefault()

    if (this.submitted) {
      this.log('Form already submitted, ignoring duplicate submission')
      return
    }

    this.clearError()
    this.showLoader()

    // Validate reservation BEFORE charging (only if a validate URL is configured)
    if (this.validateUrlValue) {
      const valid = await this.validateReservation()
      if (!valid) {
        return // Error already shown by validateReservation
      }
    }

    this.log('=== STRIPE CONFIRMATION DEBUG ===')
    this.log('Client Secret:', this.clientSecretValue)
    this.log('Stripe Account:', this.stripeAccountValue)
    this.log('Stripe Public Key:', this.publishableKeyValue)

    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    })

    this.log('Stripe confirmation result:', { error, paymentIntent })

    if (error) {
      this.hideLoader()
      this.showError(error.message)
    } else if (paymentIntent) {
      this.submitted = true
      this.submitToServer(paymentIntent)
    }
  }

  async validateReservation() {
    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        },
      })

      const data = await response.json()

      if (!data.valid) {
        this.hideLoader()
        this.showError(data.message)
        return false
      }

      return true
    } catch (error) {
      this.log('Validation check failed:', error)
      this.hideLoader()
      this.showError('Unable to verify reservation availability. Please try again.')
      return false
    }
  }

  submitToServer(paymentIntent) {
    this.log('Submitting payment intent to server:', paymentIntent.id)

    // Create hidden input with payment intent ID
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'paymentIntentId')
    hiddenInput.setAttribute('value', paymentIntent.id)
    this.element.appendChild(hiddenInput)

    // Submit the form
    this.element.submit()
  }

  showLoader() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.classList.add('disabled')
    }

    if (this.modal) {
      this.modal.show()
    }
  }

  hideLoader() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.classList.remove('disabled')
    }

    if (this.modal) {
      this.modal.hide()
    }
  }

  showError(message) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = message
      this.errorContainerTarget.classList.remove('d-none')
    }
  }

  clearError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.textContent = ''
      this.errorContainerTarget.classList.add('d-none')
    }
  }

  log(...args) {
    if (this.debugValue) {
      console.log('[StripePayment]', ...args)
    }
  }

  disconnect() {
    // Cleanup: unmount payment element
    if (this.paymentElement) {
      this.paymentElement.unmount()
    }

    // Cleanup: dispose of modal
    if (this.modal) {
      this.modal.dispose()
    }
  }
}
